/* eslint-disable indent */
import React, { Fragment, useEffect, useState } from 'react';
import { Anchor, AvatarIcon, Button, Chip, DropdownSelect, GridItemLayout, GridLayout, Modal, PageHeader, useMatchScreenWidth } from '@keplerco/core';
import { ConfirmPeerRatingsModalLayout } from '../confirm-peer-ratings-modal/confirm-peer-ratings-modal.layout';
import { EmptyState } from '../../../../../components/general/empty-state/empty-state';
import { CompletionStatus, FetchType } from '../../../../../enums';
import { filteredColours } from '../../../../../library/consts/filtered-colours';
import { Navigate } from 'react-router';
import { PageLoaderLayer } from '../../../../../components/general/loading-state/loader-layers/page-loader-layer/page-loader-layer';
import { PagePath } from '../../../../../navigation/navigation.enums';
import { RoutingMapResult } from '../../../../../models/routing-map';
import { TeamMemberReviewResource } from '../../../../../models/team-member-review-resource';
import { useAppActions, useAppState } from '../../../../../overmind';
import { AssessmentSubType } from '../../../../../enums/assessment-type';
import styles from './peer-skills-assessment.module.css';
import classNames from 'classnames';
import KeplerNavlink from '../../../../../navigation/guards/kepler-navlink';
import { useAnalysisBasePagePath } from '../../../use-analysis-base-page-path';
import { AssessmentConfig } from '../../../../../overmind/actions';
import { useKeplerNavigate } from '../../../../../navigation/guards/use-kepler-navigate';

enum FilterStatus {
  All = 'All',
  Completed = 'Completed',
  NotCompleted = 'NotCompleted',
}

export function PeerSkillsAssessmentPage(): JSX.Element {
  const keplerNavigate = useKeplerNavigate();
  const basePagePath = useAnalysisBasePagePath();

  const actions = useAppActions();
  const { activeAssessmentConfig, forcedAssessments } = useAppState();

  const [team, setTeam] = useState<TeamMemberReviewResource[]>();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [filterStatus, setFilterStatus] = useState<FilterStatus>(FilterStatus.All);

  const isMobile = useMatchScreenWidth('mobile');
  const isTablet = useMatchScreenWidth('tablet');

  useEffect(() => {
    async function getData() {
      actions.startLoader({ path: PagePath.analysisPeerSkillsAssessment, type: FetchType.PageFetching });
      const data = await actions.getTeamReviews();
      setTeam(data);
      actions.stopLoader(PagePath.analysisPeerSkillsAssessment);
    }
    getData();
  }, []);

  if (activeAssessmentConfig?.assessmentSubType !== AssessmentSubType.GroupAssessment && activeAssessmentConfig?.assessmentSubType !== AssessmentSubType.RatingAssessment) return <Navigate to={PagePath.analysisBase} />;

  let columns = 3;
  if (isMobile) columns = 1;
  else if (isTablet) columns = 2;

  const teamMembers = team?.filter(member => member.completionStatus !== CompletionStatus.NoActionRequired).sort((a, b) => a.firstName.localeCompare(b.firstName));

  async function onClickHandler(renderProps: RoutingMapResult) {
    if (!activeAssessmentConfig?.assessmentSlug) return;

    actions.startLoader({ path: PagePath.analysisPeerSkillsAssessment, type: FetchType.Sending });

    await actions.submitTeamReviews();
    await actions.completeFreemiumAssessment();
    await actions.getFreemiumAccount();
    const response = await actions.getAssessmentConfig(activeAssessmentConfig!.assessmentSlug);

    const isSelfPeerReviewComplete = response?.selfPeerReviewCompletionStatus === CompletionStatus.Completed;

    if (isSelfPeerReviewComplete) {
      const completedAssessmentSlug = activeAssessmentConfig?.assessmentSlug;

      const updatedForcedAssessments = await actions.getForcedAssessments();

      const remainingForcedAssessments = updatedForcedAssessments.filter(
        assessment => assessment.assessmentSlug !== completedAssessmentSlug
      );
      actions.clearAssessmentConfig();


      if (remainingForcedAssessments.length > 0) {
        if (remainingForcedAssessments.length === 1) {
          keplerNavigate(PagePath.analysisBase + PagePath.analysisAssessment.replace(':assessmentSlug', remainingForcedAssessments[0].assessmentSlug));
        } else {
          keplerNavigate(PagePath.analysisBase + PagePath.analysisPendingAssessments);
        }
      } else {
        keplerNavigate(PagePath.yourSkills);
      }
      actions.stopLoader(PagePath.analysisPeerSkillsAssessment);
      return;
    }

    actions.stopLoader(PagePath.analysisPeerSkillsAssessment);
    keplerNavigate(renderProps.next!);
  }

  function handleFilterChange(status: FilterStatus) {
    setFilterStatus(status);
    setShowModal(false);
  }

  const filteredTeamMembers = teamMembers?.filter(member => {
    if (filterStatus === FilterStatus.All) return true;
    if (filterStatus === FilterStatus.Completed) return member.hasCompletedReview;
    if (filterStatus === FilterStatus.NotCompleted) return !member.hasCompletedReview;
    return false;
  });

  function breadcrumbConfiguration() {
    const breadcrumbs = forcedAssessments.length > 1
      ? [
        {
          name: 'Pending assessments',
          url: PagePath.analysisBase,
        },
        {
          name: activeAssessmentConfig?.assessmentName ?? 'Assessment',
          url: basePagePath,
        },
      ]
      : [
        {
          name: activeAssessmentConfig?.assessmentName ?? 'Assessment',
          url: basePagePath,
        },
      ];

    if (activeAssessmentConfig?.selfReviewCompletionStatus !== CompletionStatus.NoActionRequired) {
      breadcrumbs.push(
        {
          name: 'Frequency of use',
          url: basePagePath + PagePath.analysisFrequencyOfUse,
        },
        {
          name: 'Self assessment',
          url: basePagePath + PagePath.analysisSelfAssessment,
        },
        {
          name: `Rate your peers' skills`,
          url: basePagePath + PagePath.analysisSelfAssessment + PagePath.analysisRateYourPeersSkills,
        },
        {
          name: 'Peer skills assessment',
          url: basePagePath + PagePath.analysisSelfAssessment + PagePath.analysisPeerSkillsAssessment,
        }
      );
    } else {
      breadcrumbs.push(
        {
          name: `Rate your peers' skills`,
          url: basePagePath + PagePath.analysisRateYourPeersSkills,
        },
        {
          name: 'Peer skills assessment',
          url: basePagePath + PagePath.analysisPeerSkillsAssessment,
        }
      );
    }

    return breadcrumbs;
  }

  return (
    <React.Fragment>
      <PageLoaderLayer path={PagePath.analysisPeerSkillsAssessment}>
        <div className="pageWrapper wrapper">
          <PageHeader breadcrumbs={breadcrumbConfiguration()} title="Peer skills assessment" subtitle="Select each of the people you need to assess and rate them on their skills" type="actions" divider>
            <div className="headerActions">
              <Button type="button" arrow onClick={() => setShowModal(!showModal)}>
                Submit ratings
              </Button>
            </div>
          </PageHeader>

          {!!team && (
            <Fragment>
              <div style={{ marginTop: 30, marginBottom: 30, display: 'flex', alignItems: 'center', gap: 15, justifyContent: 'space-between ' }}>
                <h3>Peers to assess:</h3>

                <DropdownSelect
                  label="Filter by"
                  value={filterStatus.toString()}
                  options={[
                    {
                      label: 'All',
                      value: FilterStatus.All,
                      onClick: () => handleFilterChange(FilterStatus.All),
                    },
                    {
                      label: 'Completed',
                      value: FilterStatus.Completed,
                      onClick: () => handleFilterChange(FilterStatus.Completed),
                    },
                    {
                      label: 'Not Started',
                      value: FilterStatus.NotCompleted,
                      onClick: () => handleFilterChange(FilterStatus.NotCompleted),
                    },
                  ]}
                />
              </div>

              {!!filteredTeamMembers?.length ? (
                <GridLayout columnCount={columns}>
                  {filteredTeamMembers.map((member, index) => {
                    return (
                      <GridItemLayout key={member.userId}>
                        <UserNavlinkCard activeAssessmentConfig={activeAssessmentConfig} member={member} index={index} />
                      </GridItemLayout>
                    );
                  })}
                </GridLayout>
              ) : (
                <div style={{ padding: '30px 0 0 0' }}>
                  <EmptyState title="No team members" />
                </div>
              )}
            </Fragment>
          )}
        </div>
      </PageLoaderLayer>

      <Modal open={showModal} onClose={() => setShowModal(false)}>
        <ConfirmPeerRatingsModalLayout
          title={`Have you rated everyone that you work closely with?`}
          team={team}
          handleShowCompleted={() => handleFilterChange(FilterStatus.Completed)}
          handleShowNotCompleted={() => handleFilterChange(FilterStatus.NotCompleted)}
          onClickSubmit={() =>
            onClickHandler({
              next: `${basePagePath}${PagePath.analysisPeerSkillsAssessment}`,
              previous: `${basePagePath}${PagePath.analysisRateYourPeersSkills}`,
            })
          }
        />
      </Modal>
    </React.Fragment>
  );
}

function UserNavlinkCard({ member, index, activeAssessmentConfig }: { member: TeamMemberReviewResource; index: number; activeAssessmentConfig: AssessmentConfig }) {
  const basePagePath = useAnalysisBasePagePath();

  function handleNavigation() {
    if (activeAssessmentConfig?.selfReviewCompletionStatus !== CompletionStatus.NoActionRequired) {
      return `${basePagePath}${PagePath.analysisSelfAssessment}${PagePath.analysisPeerSkillsAssessmentRating.replace(':ratingSlug', member.userSlug)}`;
    } else {
      return `${basePagePath}${PagePath.analysisPeerSkillsAssessmentRating.replace(':ratingSlug', member.userSlug)}`;
    }
  }
  return (
    <>
      <KeplerNavlink to={handleNavigation()}>
        <div className={classNames('card', styles.card)}>
          {member.hasCompletedReview ? <Chip themeColour="g">Completed</Chip> : <Chip customTheme={{ backgroundColor: 'background', borderColor: 'borders', color: 'text' }}>Not Started</Chip>}

          <div className={styles.avatar}>
            <React.Fragment>{!!member.profileImageUrl ? <img src={member.profileImageUrl} /> : <AvatarIcon name={member} imageUrl={member.profileImageUrl} backgroundColour={filteredColours[index % filteredColours.length]} fontColour={'grc_14'} />}</React.Fragment>
          </div>

          <h4>
            {member.firstName} {member.lastName}
          </h4>

          <small style={{ marginBottom: 15 }}>&nbsp;{member.jobTitle}&nbsp;</small>

          {member.hasCompletedReview ? <Anchor style={{ marginTop: 'auto' }}>Change</Anchor> : <Anchor style={{ marginTop: 'auto' }}>Start</Anchor>}
        </div>
      </KeplerNavlink>
    </>
  );
}
